import { clamp } from '#shared'
import { pointPx } from './motion.ts'
import type { OsState, OsWindow } from './os-state.ts'

/** How much of a window stays on the desktop when dragged to an edge, in points. */
const KEEP = 80

/**
 * Drags a desktop window by its title bar, from `event` (a primary press on the bar) until
 * the pointer lets go. The window stays reachable: its bar never leaves the desktop.
 */
export function startWindowDrag(
  event: PointerEvent,
  state: OsState,
  entry: OsWindow,
  frame: HTMLElement,
): void {
  const bar = event.currentTarget
  const area = frame.offsetParent
  if (entry.zoomed || event.button !== 0 || !(bar instanceof HTMLElement)) return
  if (!(area instanceof HTMLElement) || (event.target as Element).closest('button')) return
  const pt = pointPx(frame)
  const width = frame.offsetWidth / pt
  const right = area.clientWidth / pt - KEEP
  const bottom = area.clientHeight / pt - KEEP
  const start = { x: event.clientX, y: event.clientY, left: entry.x, top: entry.y }

  bar.setPointerCapture(event.pointerId)
  const controller = new AbortController()
  const { signal } = controller
  bar.addEventListener(
    'pointermove',
    (move) => {
      const x = clamp(start.left + (move.clientX - start.x) / pt, KEEP - width, right)
      const y = clamp(start.top + (move.clientY - start.y) / pt, 0, bottom)
      state.move(entry.app, x, y)
    },
    { signal },
  )
  const end = (): void => {
    controller.abort()
  }
  bar.addEventListener('pointerup', end, { signal })
  bar.addEventListener('pointercancel', end, { signal })
}
